import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import { Box, IconButton } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { InvestmentCaseContext } from "Components/Analyses/_Shared/InvestmentCaseContext";
import { BlTooltip } from "Components/_Shared/BlTooltip";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { HStack } from "Components/_Shared/Core/Stack";
import { DialogModal } from "Components/_Shared/DialogModal";
import {
  getGetAnalysisGoalsQueryKey,
  getGetFamilyGoalQueryKey,
  useDeleteGoal,
  useUpdateGoalLifecycleStatus,
  useUpdateGoalProgressStatus,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { type GoalOverview, GoalOverviewLifecycleStatus, GoalOverviewProgressStatus, GoalOverviewType } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useState } from "react";
import { toast } from "sonner";
import { type GoalActionItem, type GoalActionTone, GoalActionsMenu } from "./GoalActionsMenu";

export const GOAL_CARD_ACTIONS_CLASS = "goal-card-actions";

const ACTION_SIZE = 32;
/** Room for all four actions, so the header doesn't shift when they appear. */
const ACTIONS_SLOT_WIDTH = 136;

const Actions = Resources.AnalysisDetail.Goals.Timeline.Actions;

const TONE_SX = {
  default: { "&:hover": { bgcolor: "primary.light", color: "primary.main" } },
  danger: { "&:hover": { bgcolor: "error.light", color: "error.main" } },
  on: { bgcolor: "success.light", color: "success.dark", "&:hover": { bgcolor: "success.light" } },
} satisfies Record<GoalActionTone, object>;

const GoalAction = ({ text, icon, tone, disabled, onClick }: Omit<GoalActionItem, "key"> & { disabled: boolean }) => {
  const { t } = useResource();
  const title = t(text.Title);

  return (
    <BlTooltip
      enterDelay={300}
      enterNextDelay={300}
      title={
        <>
          <Box component="span" display="block" fontWeight={700}>
            {title}
          </Box>
          {t(text.Description)}
        </>
      }
    >
      {/* the wrapper keeps the tooltip working while the button is disabled */}
      <span>
        <IconButton
          aria-label={title}
          disabled={disabled}
          onClick={onClick}
          sx={{
            width: ACTION_SIZE,
            height: ACTION_SIZE,
            borderRadius: Radius.M,
            color: "text.secondary",
            "& .MuiSvgIcon-root": { fontSize: 18 },
            ...TONE_SX[tone],
          }}
        >
          {icon}
        </IconButton>
      </span>
    </BlTooltip>
  );
};

type Props = {
  goal: GoalOverview;
  onEdit?: () => void;
  /** Tablets and phones get the actions in a menu, they have no room for the row of buttons nor hover to reveal it. */
  isTablet: boolean;
};

export const GoalCardActions = ({ goal, onEdit, isTablet }: Props) => {
  const { guid, goalId, type, name, progressStatus, lifecycleStatus } = goal;
  const { analysisId, versionId, readOnly: isAnalysisReadOnly } = useContext(AnalysisDetailContext);
  const { isInvestmentCaseLocked } = useContext(InvestmentCaseContext);
  const { t } = useResource();
  const queryClient = useQueryClient();
  const deleteGoalMutation = useDeleteGoal();
  const updateLifecycleMutation = useUpdateGoalLifecycleStatus();
  const updateProgressMutation = useUpdateGoalProgressStatus();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  if (isAnalysisReadOnly || isInvestmentCaseLocked) {
    return null;
  }

  const isCompleted = progressStatus === GoalOverviewProgressStatus.completed;
  const isArchived = lifecycleStatus === GoalOverviewLifecycleStatus.archived;
  const isPending = deleteGoalMutation.isPending || updateLifecycleMutation.isPending || updateProgressMutation.isPending;

  const invalidateGoals = () => queryClient.invalidateQueries({ queryKey: getGetAnalysisGoalsQueryKey(analysisId, versionId) });
  const showError = () => toast.error(t(Resources.AnalysisDetail.Toast.GoalError));

  const toggleCompleted = () =>
    updateProgressMutation.mutate(
      {
        analysisId,
        versionId,
        goalId: guid,
        progressStatus: isCompleted ? GoalOverviewProgressStatus.in_progress : GoalOverviewProgressStatus.completed,
      },
      { onSuccess: invalidateGoals, onError: showError },
    );

  const setLifecycleStatus = (lifecycleStatus: GoalOverviewLifecycleStatus) =>
    updateLifecycleMutation.mutate(
      { analysisId, versionId, goalId: guid, lifecycleStatus },
      { onSuccess: invalidateGoals, onError: showError },
    );

  const deleteGoal = () =>
    deleteGoalMutation.mutate(
      { analysisId, versionId, goalId: guid },
      {
        onSuccess: () => {
          invalidateGoals();
          if (type === GoalOverviewType.children) {
            queryClient.invalidateQueries({ queryKey: getGetFamilyGoalQueryKey(analysisId, versionId, goalId) });
          }
        },
        onError: showError,
        onSettled: () => setIsDeleteDialogOpen(false),
      },
    );

  const actions: (GoalActionItem | false | undefined)[] = [
    !isArchived && {
      key: "completed",
      text: isCompleted ? Actions.MarkNotCompleted : Actions.MarkCompleted,
      icon: <CheckCircleOutlineIcon />,
      tone: isCompleted ? "on" : "default",
      onClick: toggleCompleted,
    },
    isArchived
      ? {
          key: "lifecycle",
          text: Actions.Unarchive,
          icon: <UnarchiveOutlinedIcon />,
          tone: "default",
          onClick: () => setLifecycleStatus(GoalOverviewLifecycleStatus.active),
        }
      : {
          key: "lifecycle",
          text: Actions.Archive,
          icon: <Inventory2OutlinedIcon />,
          tone: "default",
          onClick: () => setLifecycleStatus(GoalOverviewLifecycleStatus.archived),
        },
    onEdit && { key: "edit", text: Actions.Edit, icon: <EditOutlinedIcon />, tone: "default", onClick: onEdit },
    { key: "delete", text: Actions.Delete, icon: <DeleteOutlineIcon />, tone: "danger", onClick: () => setIsDeleteDialogOpen(true) },
  ];
  const availableActions = actions.filter((action): action is GoalActionItem => !!action);

  return (
    // The actions sit inside the clickable card, so their clicks must not open the goal. React bubbles events
    // through portals too, so this also covers clicks inside the menu and the delete dialog.
    <Flex ml="auto" flexShrink={0} onClick={(event) => event.stopPropagation()}>
      {isTablet ? (
        <GoalActionsMenu actions={availableActions} disabled={isPending} />
      ) : (
        <HStack
          className={GOAL_CARD_ACTIONS_CLASS}
          width={ACTIONS_SLOT_WIDTH}
          height={ACTION_SIZE}
          justifyContent="flex-end"
          gap={Spacing.XS}
        >
          {availableActions.map(({ key, ...action }) => (
            <GoalAction key={key} {...action} disabled={isPending} />
          ))}
        </HStack>
      )}

      <DialogModal
        size="sm"
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        isPending={deleteGoalMutation.isPending}
        closeLabel={t(Resources.Common.Cancel)}
        title={t(Actions.DeleteConfirm.Title)}
        subtitle={t(Actions.DeleteConfirm.Text, { name })}
        primaryAction={
          <Button loading={deleteGoalMutation.isPending} onClick={deleteGoal}>
            {t(Resources.Common.Delete)}
          </Button>
        }
      />
    </Flex>
  );
};
